import { apiFetch } from './client';
import type { PortfolioItem, PortfolioCategory, PortfolioDetail } from '@/types';

// Bypass UE and serve portfolio data from public/portfolios/portfolios.json.
// Controlled by VITE_USE_LOCAL_PORTFOLIO (default: true). Set to "false" to use BFF/UE.
const USE_LOCAL_PORTFOLIO =
  (import.meta.env.VITE_USE_LOCAL_PORTFOLIO ?? 'true').toString().toLowerCase() !== 'false';

type LocalData = { categories: PortfolioCategory[]; items: PortfolioDetail[] };

let localCache: Promise<LocalData> | null = null;
const loadLocal = () =>
  (localCache ??= apiFetch<LocalData>('/portfolios/portfolios.json'));

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const getPortfolios = async (category?: string): Promise<PortfolioItem[]> => {
  if (USE_LOCAL_PORTFOLIO) {
    const { items } = await loadLocal();
    return category
      ? items.filter(p => p.categories.some(c => slugify(c.nameEn) === category))
      : items;
  }
  const query = category ? `?category=${encodeURIComponent(category)}` : '';
  return apiFetch<PortfolioItem[]>(`/api/portfolios${query}`);
};

export const getPortfolio = async (slug: string): Promise<PortfolioDetail> => {
  if (USE_LOCAL_PORTFOLIO) {
    const { items } = await loadLocal();
    const found = items.find(p => p.slug === slug);
    if (!found) throw new Error('Portfolio not found.');
    return found;
  }
  return apiFetch<PortfolioDetail>(`/api/portfolios/${slug}`);
};

export const getPortfolioCategories = async (): Promise<PortfolioCategory[]> => {
  if (USE_LOCAL_PORTFOLIO) {
    return (await loadLocal()).categories;
  }
  return apiFetch<PortfolioCategory[]>('/api/portfolio/categories');
};
